import { Languages, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TranslateConfig } from '@new-signal/shared';
import { cn } from '../lib/utils';

interface TranslateCardProps {
  config: TranslateConfig;
  onChange: (config: TranslateConfig) => void;
  onRemove: () => void;
  error?: string | null;
}

export function TranslateCard({ config, onChange, onRemove, error }: TranslateCardProps) {
  const { t } = useTranslation('feed-builder');

  const setSource = (source: TranslateConfig['source']) => {
    onChange({ ...config, source });
  };

  const setDisplay = (display: TranslateConfig['display']) => {
    onChange({ ...config, display });
  };

  return (
    <div className="mb-4 rounded-xl border border-brand-border bg-brand-canvas p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Languages size={17} strokeWidth={1.75} className="text-accent-blue" aria-hidden />
          <strong>{t('translate.title')}</strong>
        </div>
        <button
          type="button"
          onClick={onRemove}
          title={t('translate.removeAction')}
          aria-label={t('translate.removeAction')}
          className="inline-flex size-7 cursor-pointer items-center justify-center rounded-md border-none bg-transparent text-brand-muted hover:bg-brand-surface hover:text-brand-text"
        >
          <X size={15} strokeWidth={1.75} aria-hidden />
        </button>
      </div>

      <label className="mb-3 block">
        <span className="mb-1.5 block text-sm text-brand-subtle">{t('translate.targetLanguage')}</span>
        <select
          value={config.target_language}
          onChange={(e) =>
            onChange({
              ...config,
              target_language: e.target.value as TranslateConfig['target_language'],
            })
          }
          className="box-border min-h-9 w-full rounded-lg border border-brand-border-medium bg-white px-3 text-sm text-brand-text"
        >
          <option value="en">{t('translate.target.en')}</option>
          <option value="zh-CN">{t('translate.target.zh-CN')}</option>
        </select>
      </label>

      <div className="mb-3">
        <span className="mb-1.5 block text-sm text-brand-subtle">{t('translate.sourceLabel')}</span>
        <div className="grid grid-cols-2 rounded-lg border border-brand-border-medium bg-white p-0.5">
          {(['summary', 'content'] as const).map((source) => (
            <button
              key={source}
              type="button"
              onClick={() => setSource(source)}
              className={cn(
                'min-h-9 cursor-pointer rounded-md border-none px-3 text-sm font-medium',
                config.source === source
                  ? 'bg-accent-blue text-brand-bg'
                  : 'bg-transparent text-brand-text hover:bg-brand-surface',
              )}
            >
              {t(`translate.source.${source}`)}
            </button>
          ))}
        </div>
      </div>

      <div>
        <span className="mb-1.5 block text-sm text-brand-subtle">{t('translate.displayLabel')}</span>
        <div className="grid grid-cols-2 rounded-lg border border-brand-border-medium bg-white p-0.5">
          {(['bilingual', 'translated_only'] as const).map((display) => (
            <button
              key={display}
              type="button"
              onClick={() => setDisplay(display)}
              className={cn(
                'min-h-9 cursor-pointer rounded-md border-none px-3 text-sm font-medium',
                config.display === display
                  ? 'bg-accent-blue text-brand-bg'
                  : 'bg-transparent text-brand-text hover:bg-brand-surface',
              )}
            >
              {t(`translate.display.${display}`)}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <p className="mt-2 rounded-md border border-accent-error-border bg-accent-error-bg px-2.5 py-2 text-[12px] leading-4 text-accent-error-text">
          {error}
        </p>
      )}
    </div>
  );
}
